import React, { useState } from 'react';
import { Zap, ArrowRight, Check, MessageCircle, Clock, MapPin, Users, Footprints, Medal, Timer } from 'lucide-react';
import { Button } from './ui/button';
import BookingCalendar from './BookingCalendar';
import { Seo } from './Seo';

const VIDEO_URL = 'https://customer-assets-gfyr7b9c.emergentagent.net/job_4d2f52c7-22cd-4150-9059-ad99d56ca7fb/artifacts/btq5zuyn_XiaoYing_Video_1790867839342_HD.mp4';

const SHARE_MSG = `Tight legs from running? 🏃\n\nDublin Holistic Massage specialises in sports recovery — deep calf, hamstring & quad work that releases tight muscles fast. Trusted by competing Masters athletes.\n\n60 min — €120 (€102 when you pay in full online, 15% off!)\n90 min — €150 (€127.50 paid in full)\n\nBonus: book Mon-Fri 12-3pm and the 10% Lunchtime Deal applies instead.\n\nSee it in action & book: https://www.dublinholisticmassages.com/sports-recovery`;

const PAIN_POINTS = [
  { icon: Footprints, title: 'Tight Calves & Shins', text: 'Deep, precise work on calves and shins that releases post-run tightness and helps prevent shin splints.' },
  { icon: Timer, title: 'Faster Recovery', text: 'Flush out lactic acid and improve circulation so your legs feel fresh for the next training session.' },
  { icon: Medal, title: 'Race Preparation', text: 'Pre-race loosening and post-race repair — ideal around the Dublin Marathon, park runs and club races.' },
  { icon: Users, title: 'Running Clubs Welcome', text: 'Training with a club? We have 3 massage tables per slot — book together after your long run.' },
];

const PRICING = [
  { name: '60 Minute Sports Recovery', price: 120, full: 102, note: 'Perfect post-long-run reset' },
  { name: '90 Minute Deep Recovery', price: 150, full: 127.5, note: 'Full legs, back & shoulders', popular: true },
];

const SportsRecoveryPage = () => {
  const [bookingOpen, setBookingOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#0a0f0d]" data-testid="sports-recovery-page">
      <Seo
        title="Sports Recovery Massage Dublin | Tight Legs from Running | Dublin Holistic Massage"
        description="Sports recovery massage in Dublin city centre — deep calf, hamstring and quad work for runners. Trusted by Masters athletes. 60min €120, save 15% paying in full online. Book now!"
      />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-96 h-96 bg-luxury-gold/5 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-6 relative">
          <a href="/" className="inline-flex items-center gap-2 text-alabaster-200/70 hover:text-luxury-gold text-xs uppercase tracking-widest transition-colors" data-testid="sports-page-home-link">
            <ArrowRight size={14} className="rotate-180" />
            Dublin Holistic Massage — Home
          </a>
        </div>
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-10 pb-16 relative">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-luxury-gold/15 text-luxury-gold px-5 py-2 rounded-full text-xs font-semibold uppercase tracking-widest mb-6 border border-luxury-gold/30">
                <Zap size={14} />
                Sports Recovery &middot; Runners
              </div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-alabaster leading-tight mb-5">
                Tight legs
                <span className="block italic text-luxury-gold/80 mt-1">from running?</span>
              </h1>
              <p className="text-alabaster-200/70 font-light max-w-md leading-relaxed mb-8">
                Marathon training, club sessions, park runs — your legs take the hit.
                Our senior therapists release sprint-tight calves, hamstrings and quads
                so you recover faster and run lighter. Watch Leslie work on Dr. Michael's
                calves after sprint training.
              </p>
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <Button
                  onClick={() => setBookingOpen(true)}
                  className="bg-luxury-gold hover:bg-luxury-gold/90 text-luxury-green rounded-full py-6 px-8 text-sm uppercase tracking-wider font-semibold group"
                  data-testid="sports-page-book-btn"
                >
                  Book Sports Recovery
                  <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button
                  onClick={() => window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(SHARE_MSG)}`, '_blank')}
                  className="bg-white/10 hover:bg-white/20 text-white rounded-full py-6 px-6 text-sm font-medium"
                  data-testid="sports-page-share-btn"
                >
                  <MessageCircle size={18} className="mr-2" />
                  Share with your club
                </Button>
              </div>
              <div className="inline-flex items-center gap-2 bg-white/5 border border-luxury-gold/25 rounded-full px-4 py-2" data-testid="sports-page-lunchtime-hint">
                <Clock size={14} className="text-luxury-gold" />
                <span className="text-xs text-alabaster-200/80">
                  Lunchtime runner? Mon&ndash;Fri 12&ndash;3pm bookings get <strong className="text-luxury-gold">10% off</strong> automatically
                </span>
              </div>
            </div>

            <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-black/50 border border-luxury-gold/20">
              <video
                src={VIDEO_URL}
                controls
                playsInline
                preload="metadata"
                className="w-full max-h-[480px] object-cover bg-black"
                data-testid="sports-page-video"
              />
              <div className="absolute top-3 left-3 bg-black/60 backdrop-blur-sm text-white text-[11px] px-3 py-1 rounded-full pointer-events-none">
                Leslie releasing sprint-tight calves
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PAIN POINTS */}
      <section className="py-16 bg-white/[0.02] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <h2 className="font-display text-lg md:text-lg text-alabaster text-center mb-10 uppercase tracking-widest">Built for Runners</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {PAIN_POINTS.map(({ icon: Icon, title, text }) => (
              <div key={title} className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-luxury-gold/40 transition-colors">
                <Icon size={24} className="text-luxury-gold mb-4" />
                <h3 className="text-alabaster font-medium mb-2 text-sm">{title}</h3>
                <p className="text-alabaster-200/60 text-xs leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 sm:px-8">
          <h2 className="font-display text-lg md:text-lg text-alabaster text-center mb-2 uppercase tracking-widest">Recovery Sessions</h2>
          <p className="text-center text-luxury-gold text-sm mb-10" data-testid="sports-page-pay-full-note">
            Pay in full online &amp; save 15% &middot; or secure with a &euro;20 deposit &amp; save 5%
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            {PRICING.map((p) => (
              <div key={p.name} className={`relative rounded-3xl p-8 text-center border ${p.popular ? 'border-luxury-gold/50 bg-luxury-gold/5' : 'border-white/10 bg-white/5'}`}>
                {p.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-luxury-gold text-luxury-green text-[10px] font-bold uppercase px-4 py-1 rounded-full tracking-wider">
                    Runners' Favourite
                  </div>
                )}
                <h3 className="font-display text-xl text-alabaster mb-1">{p.name}</h3>
                <p className="text-white/50 text-sm mb-5">{p.note}</p>
                <div className="font-display text-5xl text-luxury-gold mb-1">&euro;{p.price}</div>
                <p className="text-emerald-400 text-xs font-medium mb-6">
                  &euro;{p.full % 1 ? p.full.toFixed(2) : p.full} when you pay in full online
                </p>
                <Button
                  onClick={() => setBookingOpen(true)}
                  className="w-full bg-luxury-gold hover:bg-luxury-gold/90 text-luxury-green rounded-full py-5 text-sm font-semibold"
                  data-testid={`sports-page-book-${p.price}`}
                >
                  Book Now
                  <ArrowRight size={16} className="ml-2" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LOCATION + CTA */}
      <section className="py-16 bg-luxury-green">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-display text-3xl sm:text-4xl text-alabaster mb-4">Run in, walk out lighter</h2>
          <p className="text-alabaster-200/70 font-light mb-3 flex items-center justify-center gap-2 text-sm">
            <MapPin size={15} className="text-luxury-gold" />
            Talbot Street, Dublin 1 (city centre) &amp; Clarehall (free parking)
          </p>
          <ul className="inline-block text-left space-y-2 mb-8">
            {['Senior therapists trained in deep tissue & sports massage', 'Trusted by competing Masters athletes', '3 tables per slot — perfect for post-run groups'].map((b) => (
              <li key={b} className="flex items-start gap-2 text-alabaster-200/80 text-sm">
                <Check size={15} className="text-luxury-gold mt-0.5 flex-shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
          <div>
            <Button
              onClick={() => setBookingOpen(true)}
              className="bg-luxury-gold hover:bg-luxury-gold/90 text-luxury-green rounded-full py-6 px-10 text-sm uppercase tracking-wider font-semibold"
              data-testid="sports-page-book-cta-bottom"
            >
              Book Your Recovery Session
            </Button>
          </div>
        </div>
      </section>

      <BookingCalendar isOpen={bookingOpen} onClose={() => setBookingOpen(false)} />
    </div>
  );
};

export default SportsRecoveryPage;
